import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../config/gameConstants';
import { AnimationKeys, AudioKeys, SceneKeys, TextureKeys } from '../config/keys';
import { AudioManager } from '../managers/AudioManager';
import { DifficultyManager } from '../managers/DifficultyManager';
import { InputManager } from '../managers/InputManager';
import { ScoreManager } from '../managers/ScoreManager';

interface ObstaclePair {
  top: Phaser.Physics.Arcade.Image;
  bottom: Phaser.Physics.Arcade.Image;
  scored: boolean;
}

const BIRD_X = 108;
const GROUND_Y = GAME_HEIGHT - 74;
const OBSTACLE_WIDTH = 82;

export class GameScene extends Phaser.Scene {
  private bird!: Phaser.Physics.Arcade.Sprite;
  private ground!: Phaser.Physics.Arcade.Image;
  private obstacles!: Phaser.Physics.Arcade.Group;
  private obstaclePairs: ObstaclePair[] = [];
  private inputManager!: InputManager;
  private scoreManager = new ScoreManager();
  private difficultyManager = new DifficultyManager();
  private audioManager!: AudioManager;
  private scoreText!: Phaser.GameObjects.Text;
  private spawnTimer?: Phaser.Time.TimerEvent;
  private isGameOver = false;

  constructor() {
    super(SceneKeys.Game);
  }

  create(): void {
    this.isGameOver = false;
    this.obstaclePairs = [];
    this.scoreManager.reset();
    this.audioManager = AudioManager.get(this);

    this.add.image(0, 0, TextureKeys.PaperBackground).setOrigin(0);
    this.addBackgroundDoodles();

    this.obstacles = this.physics.add.group({
      allowGravity: false,
      immovable: true,
    });

    this.ground = this.physics.add.staticImage(GAME_WIDTH / 2, GROUND_Y + 37, TextureKeys.Ground);
    this.ground.refreshBody();

    this.bird = this.physics.add
      .sprite(BIRD_X, GAME_HEIGHT * 0.42, TextureKeys.BirdFrame1)
      .setScale(1.18)
      .setDepth(4)
      .setCollideWorldBounds(false);
    this.bird.body?.setSize(48, 38, true);
    this.bird.play(AnimationKeys.BirdFlap);

    this.scoreText = this.add
      .text(GAME_WIDTH / 2, 40, '0', {
        fontFamily: '"Trebuchet MS", "Comic Sans MS", sans-serif',
        fontSize: '54px',
        color: '#28221a',
        stroke: '#f7f0dc',
        strokeThickness: 7,
      })
      .setOrigin(0.5, 0)
      .setDepth(8);

    this.addHudControls();
    this.physics.add.collider(this.bird, this.ground, () => this.endGame());
    this.physics.add.overlap(this.bird, this.obstacles, () => this.endGame());

    this.inputManager = new InputManager(this, () => this.flap());
    this.inputManager.bind();

    this.spawnObstaclePair();
    this.scheduleNextSpawn();
  }

  update(): void {
    if (this.isGameOver) {
      return;
    }

    this.inputManager.update();
    this.bird.setAngle(Phaser.Math.Clamp(this.bird.body!.velocity.y / 18, -22, 64));

    if (this.bird.y < -40 || this.bird.y > GAME_HEIGHT + 40) {
      this.endGame();
      return;
    }

    this.updateScoring();
    this.cleanupObstacles();
  }

  private flap(): void {
    if (this.isGameOver) {
      return;
    }

    this.bird.setVelocityY(-350);
    this.bird.setAngle(-18);
    this.audioManager.play(AudioKeys.Flap);

    this.tweens.add({
      targets: this.bird,
      scaleX: 1.26,
      scaleY: 1.06,
      duration: 70,
      yoyo: true,
      ease: 'Sine.out',
    });
  }

  private spawnObstaclePair(): void {
    const settings = this.difficultyManager.getSettings(this.scoreManager.getScore());
    const gapCenter = Phaser.Math.Between(190, GROUND_Y - 180);
    const gapHalf = settings.gapSize / 2;
    const x = GAME_WIDTH + OBSTACLE_WIDTH;
    const topY = gapCenter - gapHalf - 210;
    const bottomY = gapCenter + gapHalf + 210;

    const top = this.obstacles
      .create(x, topY, TextureKeys.ObstacleTop)
      .setOrigin(0.5)
      .setVelocityX(-settings.obstacleVelocity) as Phaser.Physics.Arcade.Image;
    const bottom = this.obstacles
      .create(x, bottomY, TextureKeys.ObstacleBottom)
      .setOrigin(0.5)
      .setVelocityX(-settings.obstacleVelocity) as Phaser.Physics.Arcade.Image;

    top.body?.setSize(62, 398, true);
    bottom.body?.setSize(62, 398, true);
    top.setDepth(3);
    bottom.setDepth(3);

    this.obstaclePairs.push({ top, bottom, scored: false });
  }

  private scheduleNextSpawn(): void {
    const settings = this.difficultyManager.getSettings(this.scoreManager.getScore());
    this.spawnTimer = this.time.delayedCall(settings.spawnDelay, () => {
      if (this.isGameOver) {
        return;
      }
      this.spawnObstaclePair();
      this.scheduleNextSpawn();
    });
  }

  private updateScoring(): void {
    this.obstaclePairs.forEach((pair) => {
      if (!pair.scored && pair.top.x + OBSTACLE_WIDTH / 2 < this.bird.x) {
        pair.scored = true;
        const score = this.scoreManager.addPoint();
        this.scoreText.setText(String(score));
        this.audioManager.play(AudioKeys.Score);
      }
    });
  }

  private cleanupObstacles(): void {
    this.obstaclePairs = this.obstaclePairs.filter((pair) => {
      if (pair.top.x < -OBSTACLE_WIDTH) {
        pair.top.destroy();
        pair.bottom.destroy();
        return false;
      }
      return true;
    });
  }

  private endGame(): void {
    if (this.isGameOver) {
      return;
    }

    this.isGameOver = true;
    this.spawnTimer?.remove(false);
    this.inputManager.destroy();
    this.bird.stop();
    this.bird.setTexture(TextureKeys.BirdFrame2);
    this.bird.setAngle(78);
    this.audioManager.play(AudioKeys.Hit);

    this.obstacles.getChildren().forEach((child) => {
      const body = (child as Phaser.Physics.Arcade.Image).body;
      if (body instanceof Phaser.Physics.Arcade.Body) {
        body.setVelocityX(0);
      }
    });

    this.time.delayedCall(580, () => {
      this.audioManager.play(AudioKeys.GameOver);
      this.scene.start(SceneKeys.GameOver, {
        score: this.scoreManager.getScore(),
        bestScore: this.scoreManager.getBestScore(),
      });
    });
  }

  private addHudControls(): void {
    this.add
      .text(22, 28, 'pause', {
        fontFamily: '"Trebuchet MS", "Comic Sans MS", sans-serif',
        fontSize: '16px',
        color: '#28221a',
      })
      .setDepth(8)
      .setInteractive({ useHandCursor: true })
      .on(Phaser.Input.Events.POINTER_DOWN, () => {
        if (!this.isGameOver) {
          this.scene.start(SceneKeys.Menu);
        }
      });

    const muteText = this.add
      .text(GAME_WIDTH - 22, 28, this.audioManager.isMuted() ? 'sound off' : 'sound on', {
        fontFamily: '"Trebuchet MS", "Comic Sans MS", sans-serif',
        fontSize: '16px',
        color: '#28221a',
      })
      .setOrigin(1, 0)
      .setDepth(8)
      .setInteractive({ useHandCursor: true })
      .on(Phaser.Input.Events.POINTER_DOWN, (_pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
        event.stopPropagation();
        const muted = this.audioManager.toggleMuted();
        muteText.setText(muted ? 'sound off' : 'sound on');
      });
  }

  private addBackgroundDoodles(): void {
    this.add.image(90, 166, TextureKeys.Cloud).setAlpha(0.42);
    this.add.image(310, 256, TextureKeys.Cloud).setScale(0.72).setAlpha(0.32);
    this.add.image(72, 530, TextureKeys.Cloud).setScale(0.58).setAlpha(0.25);

    const graphics = this.add.graphics();
    graphics.lineStyle(2, 0x28221a, 0.28);
    graphics.strokeCircle(314, 112, 22);
    graphics.lineBetween(300, 98, 328, 126);
    graphics.lineBetween(328, 98, 300, 126);
    graphics.beginPath();
    graphics.moveTo(58, 732);
    graphics.lineTo(80, 710);
    graphics.lineTo(104, 734);
    graphics.strokePath();
  }
}
